import { GetStaticProps } from 'next';
import { ReactElement, ReactNode } from 'react';
import styled from 'styled-components';

import theme from '../assets/theme';
import { NextPageWithLayout } from '../components/layouts';
import LandingPageLayout from '../components/layouts/LandingPageLayout';
import { DefaultLocale } from '../providers/tolgee';

const EFFECTIVE_DATE = 'September 30th, 2026';
const PRIOR_POLICY_URL = '/legal/curipod-privacy-policy-2025-09-26.md';
const COMPLIANCE_EMAIL = 'compliance@curipod.com';

const iconProps = {
  width: 20,
  height: 20,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const CalendarIcon = () => (
  <svg {...iconProps}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

const DownloadIcon = () => (
  <svg {...iconProps}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
);

const SECTIONS = [
  { id: 'please-read-this-carefully', title: 'Please Read This Carefully' },
  { id: 'teachers', title: 'Teachers Using Curipod (Registered Users)' },
  {
    id: 'students',
    title: "Students Using Curipod (Note That Students Don't Have Accounts)",
  },
  { id: 'disclosure', title: 'How Personal Information May Be Disclosed' },
  { id: 'de-identified-data', title: 'De-Identified and Aggregated Data' },
  { id: 'data-storage', title: 'Data Storage in the United States' },
  { id: 'your-rights', title: 'Access to Your Information and Other Rights' },
  { id: 'breach-response', title: 'Breach Response Plan' },
  { id: 'ai', title: 'Personal Information and Artificial Intelligence' },
  { id: 'google-workspace', title: 'Google Workspace Data Usage' },
  { id: 'ferpa-coppa', title: 'FERPA and COPPA Compliance' },
  { id: 'additional-promises', title: 'Additional Promises' },
  { id: 'changes', title: 'Changes to the Privacy Policy' },
  { id: 'contact', title: 'How to Contact Us' },
] as const;

type SectionId = typeof SECTIONS[number]['id'];

const Section = ({ id, children }: { id: SectionId; children: ReactNode }) => (
  <PolicySection id={id} aria-labelledby={`${id}-heading`}>
    <h2 id={`${id}-heading`}>{SECTIONS.find(section => section.id === id)?.title}</h2>
    {children}
  </PolicySection>
);

const ComplianceEmail = () => (
  <a href={`mailto:${COMPLIANCE_EMAIL}`}>{COMPLIANCE_EMAIL}</a>
);

const COLUMNS = [
  'Personal Information We May Process',
  'What Is It Used For?',
  'How Long Is It Stored?',
];

const UNTIL_DELETED =
  'Until you delete your account, or reach out to us and ask for your data to be deleted';

const DataTable = ({ rows }: { rows: [ReactNode, ReactNode, ReactNode][] }) => (
  <Table>
    <thead>
      <tr>
        {COLUMNS.map(column => (
          <th key={column} scope="col">
            {column}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((row, rowIndex) => (
        <tr key={rowIndex}>
          {row.map((cell, cellIndex) => (
            <td key={cellIndex} data-label={COLUMNS[cellIndex]}>
              {cell}
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  </Table>
);

const PrivacyPolicyPage: NextPageWithLayout = () => {
  return (
    <>
      <Header>
        <HeaderInner>
          <Title>Curipod Privacy Policy</Title>
          <Divider />
          <EffectiveDate>
            <CalendarIcon />
            Effective as of {EFFECTIVE_DATE}
          </EffectiveDate>
          <DownloadLink href={PRIOR_POLICY_URL} download>
            Prior Privacy Policy
            <DownloadIcon />
          </DownloadLink>
        </HeaderInner>
      </Header>

      <Body>
        <Toc aria-label="Table of contents">
          <TocTitle>Contents</TocTitle>
          <ol>
            {SECTIONS.map(section => (
              <li key={section.id}>
                <a href={`#${section.id}`}>{section.title}</a>
              </li>
            ))}
          </ol>
        </Toc>

        <Content>
          <Section id="please-read-this-carefully">
            <p>
              This Privacy Policy applies to the use of the Curipod software and website
              (from now refered to as the &quot;<strong>Services</strong>&quot; or &quot;
              <strong>Curipod Platform</strong>&quot;), . It governs how Curipod
              (sometimes referred to as &quot;<strong>us</strong>&quot;, &quot;
              <strong>we</strong>
              &quot; or &quot;<strong>our</strong>&quot;) provide our Services to
              Customers and Users. When we talk about Customers and Users we might also
              refer to &quot;<strong>You</strong>&quot; or &quot;<strong>Your</strong>
              &quot;.
            </p>
            <p>
              We respect your privacy and are committed to maintaining and using any
              information we collect through your use of our Services responsibly and in
              accordance with FERPA &amp; COPPA &amp; GDPR
            </p>
            <p>
              Curipod will <strong>never</strong> use student data for commercial
              purposes.
            </p>
            <p>
              If you are a representative for a school, school district or an organization
              and you need a signed data processing agreement with us to comply with FERPA
              &amp; COPPA &amp; GDPR or other regulations, reach out to{' '}
              <ComplianceEmail /> and you will get access and help with any questions you
              may have.
            </p>

            <h3>Curipod as Data Controller</h3>
            <p>
              When signing up as a User for the Platform directly Curipod will act as a
              data controller. We process your data in accordance with this agreement.
            </p>
            <p>
              Note that if you are part of an Organization, a seperate data processing
              agreement might be in place where your Organization is the data controller.
              In this case, please reach out to your Organization to learn how your data
              is processed.
            </p>

            <h3>The Personal Information We May Collect</h3>
            <p>
              We may collect and process information that could be directly or indirectly
              associated with you (&quot;Personal Information&quot;, &quot;Personal
              Data&quot; ) Our primary purpose in collecting Person Information is to
              provide you with a safe, smooth, efficient, and customized experience.
              Depending on the manner in which you use our Services, we collect
              information about you that we deem necessary for providing Services and
              features that optimize, secure, and ease your user experience.
            </p>
          </Section>

          <Section id="teachers">
            <DataTable
              rows={[
                [
                  'Email and name.',
                  'For setting up your account, providing email support',
                  UNTIL_DELETED,
                ],
                [
                  'Openid',
                  "The identificator for a user's profile that belongs to the user's identity provider. This is used to connect the user's Curipod profile with the user's identity provider profile. (relevant if you sign in with Google for example)",
                  UNTIL_DELETED,
                ],
                [
                  'IP address',
                  'For error tracking to give users a better user experience',
                  'Stored temporarily, and deleted latest within 180 days after user deletion.',
                ],
                [
                  'In-app behavior such as lessons created and played',
                  'To recommend content to the user, and for us to further develop the Services',
                  UNTIL_DELETED,
                ],
                [
                  'Presentation content',
                  'You might add personal data to your lessons. We will process this safely like all other personal data.',
                  UNTIL_DELETED,
                ],
                [
                  'Payment information',
                  'To let you pay for our services',
                  'Until you delete your account, or reach out to us and ask for your data to be deleted, except for when applicable law demands storing the data.',
                ],
                [
                  'Email',
                  <>
                    If you sign up with an email address associated with an organization,
                    we may share your email address with that organization. We may send
                    marketing communications and newsletters to non-student users
                    personalized from their experience on the services such as products
                    and offers that are tailored to them. We may use email address to
                    create lookalike audiences on third-party platforms (such as Meta,
                    Google, or TikTok). This allows us to deliver more relevant ads and
                    reach new people with similar interest. Related to this, if a
                    non-student user outside of Europe visits Curipod.com directly from a
                    digital ad, we may provide that website with a confirmation that the
                    user has successfully signed up and used Curipod, to evaluate and
                    inform our marketing effort. This confirmation is limited to an
                    anonymized string (fbclid, gclid or ttcid) confirming the two events.
                    Only non-student users coming from a digital ad will be affected by
                    this. If you want to opt out, please email <ComplianceEmail />. Users
                    in the EU are opted out by default.
                  </>,
                  UNTIL_DELETED,
                ],
              ]}
            />
          </Section>

          <Section id="students">
            <DataTable
              rows={[
                [
                  'IP address',
                  'To prevent abuse and hacking.',
                  'Stored a maximum of 3 weeks.',
                ],
                [
                  'Student responses and optionally student names',
                  "To let the teacher identify students and access students' results.",
                  'Stored in teacher account until the teacher deletes their account, ask for deletion, or DPA mandates deletion.',
                ],
              ]}
            />
          </Section>

          <Section id="disclosure">
            <p>The Personal Information may be disclosed:</p>
            <p>
              To our third party service providers who provide services such as website
              hosting, data analysis, information technology and related infrastructure
              provision, customer service, email delivery, auditing and other services.
              Curipod will never use student data for commercial purposes, nor will we let
              any third parties use student data for commercial purposes.
            </p>
          </Section>

          <Section id="de-identified-data">
            <p>
              We may use de-identified and aggregated data — data from which all personal
              information, and any information that could reasonably be used to identify a
              student, teacher, or school, has been removed — for research, to improve our
              products, and to publish aggregated findings about student reading and
              writing. We do not attempt to re-identify this data, and we contractually
              prohibit any third party we share it with from doing so.
            </p>
          </Section>

          <Section id="data-storage">
            <p>
              Teacher and student data is stored in the United States. Curipod uses
              US-based data centers, so personal data for US schools and districts stays
              in the US.
            </p>
            <p>
              If you are a European customer and require your data to be stored within the
              European Union (&quot;EU&quot;)/European Economic Area (&quot;EEA&quot;),
              please contact <ComplianceEmail />.
            </p>
            <p>
              When personal data from the EU/EEA, the United Kingdom or Switzerland is
              transferred to the United States, Curipod remains fully compliant with the
              GDPR, the UK GDPR and the Swiss Federal Act on Data Protection. We rely on
              the European Commission&apos;s Standard Contractual Clauses and, where
              applicable, our sub-processors&apos; certification under the EU-U.S. Data
              Privacy Framework. You can request a copy of the applicable safeguards at{' '}
              <ComplianceEmail />.
            </p>
          </Section>

          <Section id="your-rights">
            <p>
              You are, at any time and free of charge, entitled to receive a copy of the
              personal data that we hold about You and information about the processing
              thereof. Please note that Curipod needs to be able to determine your
              identity in order to be able to respond to your request.
            </p>
            <p>
              You have the right to have Your personal data deleted on request, as soon as
              possible, and no later than 180 days after Your request. Please note that
              Curipod is not obliged to delete the personal data if Curipod can show that
              the processing is necessary, for example, for the establishment, exercise,
              or defense of a legal claim.
            </p>
            <p>
              If you provide Your email address to Curipod, you will always have the
              opportunity to opt out of receiving email notifications through the
              unsubscribe link in the email communications or by logging into and changing
              the preferences for your user account.
            </p>
          </Section>

          <Section id="breach-response">
            <p>
              In case of security incidents with a risk of affecting personal data, all
              user will be notified with a description of the security breach, who might
              be affected by the breach, what data might be affected, what measures have
              been taken to handle the security breach, and what measures are established
              to avoid a similar security breach in the future.
            </p>
          </Section>

          <Section id="ai">
            <p>
              We have implemented strong safeguards to make sure Personal Information is
              processed diligently in relations to AI models used in Curipod. We do not
              use teacher or student data to train artificial intelligence models. The
              third party AI model providers we use do not use teacher or student data
              provided by Curipod users to train their artificial intelligence models.
              When an AI is prompted in Curipod, the personal data connected to that
              account, such as email and school name, is not shared as part of the prompt
              to the model provider.
            </p>
          </Section>

          <Section id="google-workspace">
            <p>
              We allow users to connect their Google Drive accounts to upload
              presentations directly to Curipod. The content of these presentations is
              parsed and passed through an AI model to generate complementary slides and
              activities. We do not use any Google Workspace data to develop, improve, or
              train generalized/non-personalized AI/ML models. The data transferred to
              third-party AI tools is solely for the purpose of generating complementary
              content and is not used for training generalized/non-personalized AI/ML
              models.
            </p>
          </Section>

          <Section id="ferpa-coppa">
            <p>
              We comply with FERPA and COPPA and when applicable to us. When FERPA
              applies, we act as a &quot;school official&quot; to our school or school
              district customer, which means that we are under the direct control of the
              school or district and use students&apos; personal information only to
              provide our services to the school or district. To the extent COPPA applies,
              the school or district provides us with any necessary consent on behalf of
              students&apos; parents or guardians to permit use of Curipod in the
              classroom.
            </p>
          </Section>

          <Section id="additional-promises">
            <ul>
              <li>Personal information is not sold or rented to third parties.</li>
              <li>Personal information is not shared for third-party marketing.</li>
              <li>Personalised advertising is not displayed.</li>
              <li>
                Personal Information is not collected by our third party sub-processors
                for their own purposes.
              </li>
              <li>
                Curipod does not create a data profile about you for personalised
                advertisements. However, we may send marketing communications and
                newsletters to non-student users personalized from their experience on the
                services such as products and offers that are tailored to them
              </li>
              <li>We encrypt all data in transit.</li>
              <li>We make sure all data are stored in an encrypted format.</li>
            </ul>
          </Section>

          <Section id="changes">
            <p>
              We may review and update this Privacy Policy from time to time in our sole
              discretion. As such, your continued use of the Services after receipt of
              notification of changes to this Privacy Policy is deemed to be acceptance of
              those changes. The notification will be provided in reasonable time before
              changes apply.
            </p>
          </Section>

          <Section id="contact">
            <p>
              If you have any questions about this Privacy Policy or how we handle your
              personal data, or if you want to exercise any of your rights, please contact
              us at <ComplianceEmail />.
            </p>
            <ContactCard>
              <strong>Curipod Inc</strong>
              <span>
                Email: <ComplianceEmail />
              </span>
            </ContactCard>
          </Section>
        </Content>
      </Body>
    </>
  );
};

PrivacyPolicyPage.getLayout = function getLayout(page: ReactElement) {
  return (
    <LandingPageLayout
      disableHeadline={true}
      hideActions={true}
      title="Privacy Policy"
      subTitle="Privacy Policy of Using Curipod"
    >
      {page}
    </LandingPageLayout>
  );
};

export const getStaticProps: GetStaticProps = async () => {
  return {
    props: {
      _tolgeeSSR: {
        language: DefaultLocale,
      },
    },
  };
};

export default PrivacyPolicyPage;

const Header = styled.header`
  background: ${theme.colors.black};
  color: ${theme.colors.white};
  padding: 72px 24px 64px;

  @media (max-width: 992px) {
    padding: 48px 16px 40px;
  }
`;

const HeaderInner = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Title = styled.h1`
  margin: 0;
  color: ${theme.colors.white};
  font-size: 64px;
  line-height: 1.1;
  font-weight: 700;

  @media (max-width: 992px) {
    font-size: 40px;
  }
  @media (max-width: 480px) {
    font-size: 32px;
  }
`;

const Divider = styled.hr`
  width: min(320px, 60%);
  margin: 32px 0 24px;
  border: none;
  border-top: 2px solid ${theme.colors.orange};
`;

const EffectiveDate = styled.p`
  margin: 0 0 12px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  svg {
    display: inline-block;
    vertical-align: -4px;
    margin-right: 10px;
    color: ${theme.colors.orange};
  }

  @media (max-width: 480px) {
    font-size: 13px;
    letter-spacing: 0.08em;
  }
`;

const DownloadLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  color: ${theme.colors.white};
  text-decoration: none;
  border-bottom: 1px solid transparent;
  transition: border-color 0.2s ease-in-out;

  &:hover,
  &:focus-visible {
    color: ${theme.colors.white};
    border-bottom-color: ${theme.colors.orange};
  }
`;

const Body = styled.div`
  background: ${theme.colors.background};
  display: grid;
  grid-template-columns: 280px minmax(0, 760px);
  justify-content: center;
  gap: 56px;
  padding: 56px 24px 96px;

  @media (max-width: 992px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 32px;
    padding: 32px 16px 64px;
  }
`;

const Toc = styled.nav`
  position: sticky;
  top: 100px;
  align-self: start;
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  background: ${theme.colors.paleBeige};
  border-radius: 10px;
  padding: 24px;

  ol {
    margin: 0;
    padding-left: 20px;
  }

  li {
    font-size: 15px;
    line-height: 1.4;
    margin-bottom: 10px;
    color: ${theme.colors.subtitleGrey};
  }

  a {
    color: ${theme.colors.black};
    text-decoration: none;

    &:hover,
    &:focus-visible {
      color: ${theme.colors.orangeDark};
      text-decoration: underline;
    }
  }

  @media (max-width: 992px) {
    position: static;
    max-height: none;
  }
`;

const TocTitle = styled.p`
  margin: 0 0 16px;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${theme.colors.subtitleGrey};
`;

const Content = styled.main`
  min-width: 0;
  color: ${theme.colors.black};

  p,
  li {
    font-size: 18px;
    line-height: 1.7;
  }

  a {
    color: ${theme.colors.orangeDark};
    word-break: break-word;
  }

  ul {
    padding-left: 24px;
  }

  li {
    margin-bottom: 8px;
  }

  h3 {
    font-size: 20px;
    margin: 32px 0 12px;
  }

  @media (max-width: 480px) {
    p,
    li {
      font-size: 16px;
    }
  }
`;

const PolicySection = styled.section`
  scroll-margin-top: 100px;
  padding-bottom: 40px;
  margin-bottom: 40px;
  border-bottom: 1px solid ${theme.colors.divider};

  &:last-child {
    border-bottom: none;
  }

  h2 {
    font-size: 28px;
    line-height: 1.25;
    margin: 0 0 20px;

    @media (max-width: 480px) {
      font-size: 22px;
    }
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  background: ${theme.colors.white};
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  font-size: 16px;
  line-height: 1.5;

  th,
  td {
    text-align: left;
    vertical-align: top;
    padding: 14px 16px;
    border-bottom: 1px solid ${theme.colors.divider};
  }

  th {
    background: ${theme.colors.paleBeige};
    font-weight: 700;
  }

  th:nth-child(1) {
    width: 26%;
  }
  th:nth-child(2) {
    width: 44%;
  }
  th:nth-child(3) {
    width: 30%;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  td:first-child {
    font-weight: 600;
  }

  @media (max-width: 992px) {
    thead {
      display: none;
    }

    tr,
    td {
      display: block;
    }

    tbody tr {
      border-bottom: 1px solid ${theme.colors.divider};
    }

    tbody tr:last-child {
      border-bottom: none;
    }

    td {
      border-bottom: none;
      padding: 6px 16px;
    }

    td:first-child {
      padding-top: 16px;
    }

    td:last-child {
      padding-bottom: 16px;
    }

    td::before {
      content: attr(data-label);
      display: block;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      color: ${theme.colors.subtitleGrey};
      margin-bottom: 2px;
    }
  }
`;

const ContactCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  background: ${theme.colors.paleBeige};
  border-radius: 10px;
  padding: 20px 24px;
  font-size: 18px;
`;
